import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import WaveDivider from '@/components/WaveDivider';
import latestBg from '@/assets/latest-news-bg.jpg';
import HomeHeroCarousel from '@/components/HomeHeroCarousel';
import { Link } from 'react-router-dom';
import { Bus, Heart, Users, Calendar, ArrowRight, MapPin, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import SEO from '@/components/SEO';
import { Helmet } from 'react-helmet-async';
import DataLoadingSkeleton from '@/components/DataLoadingSkeleton';
import DataLoadingError from '@/components/DataLoadingError';
import { optimiseImageUrl } from '@/lib/image-utils';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': ['NGO', 'LocalBusiness'],
  '@id': 'https://fishvolunteers.org/#organization',
  name: 'FISH Volunteer Centre',
  alternateName: 'FISH',
  url: 'https://fishvolunteers.org',
  logo: 'https://fishvolunteers.org/favicon.png',
  image: 'https://fishvolunteers.org/og-image.png',
  description: 'FISH Volunteer Centre provides free, friendly community transport, volunteer drivers, and home visiting to residents of Sonning Common and surrounding villages — run entirely by volunteers.',
  telephone: '+441189723986',
  email: 'office@fishvolunteercentre.org.uk',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Lea Road',
    addressLocality: 'Sonning Common',
    addressRegion: 'Oxfordshire',
    postalCode: 'RG4 9LJ',
    addressCountry: 'GB',
  },
  areaServed: [
    { '@type': 'Place', name: 'Sonning Common' },
    { '@type': 'Place', name: 'Kidmore End' },
    { '@type': 'Place', name: 'Peppard' },
    { '@type': 'Place', name: 'Rotherfield Peppard' },
    { '@type': 'Place', name: 'Chazey Heath' },
    { '@type': 'Place', name: 'Gallowstree Common' },
  ],
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:30',
      closes: '11:30',
    },
  ],
  nonprofitStatus: 'NonprofitType',
  identifier: {
    '@type': 'PropertyValue',
    propertyID: 'Charity Commission for England and Wales',
    value: '1189325',
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Community Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Minibus Service', url: 'https://fishvolunteers.org/services/minibus' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Volunteer Drivers', url: 'https://fishvolunteers.org/services/volunteer-drivers' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Home Visiting', url: 'https://fishvolunteers.org/services/home-visiting' } },
    ],
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': 'https://fishvolunteers.org/#website',
  url: 'https://fishvolunteers.org',
  name: 'FISH Volunteer Centre',
  publisher: { '@id': 'https://fishvolunteers.org/#organization' },
  inLanguage: 'en-GB',
};

type NewsArticle = {
  id: string;
  slug: string | null;
  title: string;
  excerpt: string | null;
  thumbnail_url: string | null;
  image_url: string | null;
  image_alt_text: string | null;
  published_at: string | null;
};

type EventItem = {
  id: string;
  slug: string | null;
  title: string;
  description: string | null;
  event_date: string;
  event_time: string | null;
  location: string | null;
  image_url: string | null;
  image_alt_text: string | null;
};

function formatDate(dateStr: string | null) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

const services = [
  {
    icon: <Bus className="w-10 h-10" />,
    title: 'Minibus Service',
    description: 'Accessible transport for weekly shopping trips and local leisure outings. Our purpose-built bus includes a wheelchair lift to aid access.',
    to: '/services/minibus',
  },
  {
    icon: <Heart className="w-10 h-10" />,
    title: 'Volunteer Drivers',
    description: 'Reliable, friendly transport for your medical appointments and other essential journeys. Our local volunteers provide a door-to-door service that helps you stay mobile and independent.',
    to: '/services/volunteer-drivers',
  },
  {
    icon: <Users className="w-10 h-10" />,
    title: 'Home Visiting',
    description: 'A befriending service for those who, for all sorts of reasons, may be lonely and isolated and would benefit from social contact. We match you with a friendly volunteer who will visit regularly for a chat and a cuppa.',
    to: '/services/home-visiting',
  },
];

export default function HomePage() {
  const [latestNews, setLatestNews] = useState<NewsArticle[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchData = () => {
    setLoading(true);
    setError(false);
    Promise.all([
      supabase
        .from('news_articles')
        .select('id, slug, title, excerpt, thumbnail_url, image_url, image_alt_text, published_at')
        .eq('is_published', true)
        .order('published_at', { ascending: false })
        .limit(4),
      supabase
        .from('events')
        .select('id, slug, title, description, event_date, event_time, location, image_url, image_alt_text')
        .eq('is_published', true)
        .gte('event_date', new Date().toISOString().split('T')[0])
        .order('event_date', { ascending: true })
        .limit(4),
    ]).then(([newsRes, eventRes]) => {
      if (newsRes.error && eventRes.error) {
        setError(true);
      } else {
        setLatestNews((newsRes.data ?? []) as NewsArticle[]);
        setUpcomingEvents((eventRes.data ?? []) as EventItem[]);
      }
      setLoading(false);
    }).catch(() => { setError(true); setLoading(false); });
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <Layout>
      <SEO
        title="Community Transport &amp; Support in Sonning Common"
        description="Free volunteer-run transport, driver service, and home visiting for residents of Sonning Common and surrounding villages."
        canonicalPath="/"
      />
      <Helmet>
        <title>FISH Volunteer Centre — Community Transport, Sonning Common</title>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      </Helmet>
      <section className="relative overflow-hidden">
        <HomeHeroCarousel />
        <WaveDivider color="hsl(var(--background))" bgColor="bg-transparent" className="absolute -bottom-px left-0 w-full" />
      </section>

      {/* Intro */}
      <section className="pt-12 pb-10 px-4 bg-background">
        <div className="container mx-auto text-center max-w-3xl">
          <h1 className="text-3xl md:text-5xl font-bold text-center text-primary mb-4">
            Helping Our Community Stay Independent
          </h1>
          <p className="text-center text-lg text-foreground/80 leading-relaxed max-w-3xl mx-auto mb-10">
            Welcome to FISH, a dedicated charity committed to enhancing the lives of those in Sonning Common and the surrounding villages. We offer practical support and companionship to individuals in need, ensuring that everyone in our community feels valued and connected.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-cerulean hover:bg-cerulean/90 text-white text-xl px-8 py-6 rounded-lg shadow-lg">
              <Link to="/contact?type=service">I Need Help</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground bg-transparent text-xl px-8 py-6 rounded-lg">
              <Link to="/contact?type=volunteer">I Want to Volunteer</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="pt-6 pb-10 px-4 bg-muted">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-primary mb-4">Our Services</h2>
          <p className="text-center text-muted-foreground text-lg mb-12 max-w-2xl mx-auto">
            Our charity provides a wide range of services, including volunteer drivers for those requiring transport, home visiting companionship, and accessible transport through our minibus service.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map(({ icon, title, description, to }) => (
              <Link key={title} to={to} className="bg-card border border-border rounded-xl p-8 flex flex-col items-start gap-4 shadow-sm hover:shadow-md transition-shadow group">
                <div className="text-cerulean">{icon}</div>
                <h3 className="text-2xl font-bold text-primary group-hover:text-cerulean transition-colors">{title}</h3>
                <p className="text-foreground/80 text-lg leading-relaxed">{description}</p>
                <span className="mt-auto inline-flex items-center gap-2 text-cerulean font-medium group-hover:underline">
                  Read more <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="py-16 px-4 bg-background">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">Our Mission</h2>
          <p className="text-lg text-foreground/80 leading-relaxed">
            At the heart of our work is a passion for supporting independence and fostering a sense of belonging. We strive to make a meaningful difference in the lives of our community members by delivering practical help and building lasting relationships.
          </p>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-12 px-4 bg-muted">
        <div className="container mx-auto max-w-6xl">
          {loading ? (
            <>
              <h2 className="text-3xl font-bold text-primary mb-6">Upcoming Events</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <DataLoadingSkeleton variant="cards" count={4} bare />
              </div>
            </>
          ) : error ? (
            <DataLoadingError message="We couldn't load the latest news and events right now." onRetry={fetchData} />
          ) : upcomingEvents.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-primary">Upcoming Events</h2>
                <Button asChild variant="outline" className="border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground gap-2">
                  <Link to="/news">All Events <ArrowRight className="w-4 h-4" /></Link>
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {upcomingEvents.map((event) => (
                  <Link
                    key={event.id}
                    to={`/news/events/${event.slug ?? event.id}`}
                    className="group bg-card border border-border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-row sm:flex-col"
                  >
                    {event.image_url ? (
                      <div className="relative w-32 h-auto sm:w-full sm:h-36 bg-muted overflow-hidden flex-shrink-0">
                        <img
                          src={optimiseImageUrl(event.image_url, { width: 500, height: 260 })}
                          alt={event.image_alt_text || event.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                    ) : (
                      <div className="w-32 h-auto sm:w-full sm:h-36 bg-cerulean/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-cerulean/40 text-2xl sm:text-3xl font-bold select-none">FISH</span>
                      </div>
                    )}
                    <div className="p-3 sm:p-4 flex flex-col flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1 sm:gap-1.5 mb-1.5 sm:mb-2">
                        <span className="inline-flex items-center gap-1 bg-cerulean/10 text-cerulean font-semibold text-xs px-1.5 py-0.5 rounded-full">
                          <Calendar className="w-3 h-3" />
                          {formatDate(event.event_date)}
                        </span>
                        {event.event_time && (
                          <span className="inline-flex items-center gap-1 bg-cerulean/10 text-cerulean font-semibold text-xs px-1.5 py-0.5 rounded-full">
                            <Clock className="w-3 h-3" />
                            {event.event_time}
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm sm:text-base font-bold text-primary leading-snug group-hover:text-cerulean transition-colors line-clamp-2 mb-1">
                        {event.title}
                      </h3>
                      {event.location && (
                        <span className="inline-flex items-center gap-1 text-cerulean/80 text-xs mb-2">
                          <MapPin className="w-3 h-3" />
                          {event.location}
                        </span>
                      )}
                      <span className="mt-auto inline-flex items-center gap-1.5 text-xs font-medium text-cerulean group-hover:underline">
                        Read more <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </section>

      {/* Latest News */}
      <section className="py-12 px-4 bg-background">
        <div className="container mx-auto max-w-6xl">
          {loading ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-primary">Latest News</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                <DataLoadingSkeleton variant="cards" count={4} bare />
              </div>
            </>
          ) : latestNews.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold text-primary">Latest News</h2>
                <Button asChild variant="outline" className="border-primary text-primary bg-transparent hover:bg-primary hover:text-primary-foreground gap-2">
                  <Link to="/news">All News <ArrowRight className="w-4 h-4" /></Link>
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
                {latestNews.map((article) => (
                  <Link
                    key={article.id}
                    to={`/news/${article.slug ?? article.id}`}
                    className="group bg-card border border-border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-row sm:flex-col"
                  >
                    <div className="relative w-32 h-auto sm:w-full sm:h-44 bg-muted overflow-hidden flex-shrink-0">
                      {(article.thumbnail_url || article.image_url) ? (
                        <img
                          src={optimiseImageUrl(article.thumbnail_url ?? article.image_url ?? '', { width: 500, height: 350 })}
                          alt={article.image_alt_text || article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-cerulean/10">
                          <span className="text-cerulean/40 text-3xl sm:text-5xl font-bold select-none">FISH</span>
                        </div>
                      )}
                    </div>
                    <div className="p-4 sm:p-5 flex flex-col flex-1 min-w-0">
                      {article.published_at && (
                        <div className="flex items-center gap-1.5 text-muted-foreground text-xs sm:text-sm mb-1.5 sm:mb-2">
                          <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          {formatDate(article.published_at)}
                        </div>
                      )}
                      <h3 className="text-sm sm:text-base font-bold text-primary leading-snug group-hover:text-cerulean transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      {article.excerpt && (
                        <p className="text-foreground/70 text-xs sm:text-sm leading-relaxed line-clamp-2 mt-1.5 sm:mt-2 flex-1">
                          {article.excerpt}
                        </p>
                      )}
                      <span className="hidden sm:inline-flex self-start items-center gap-2 text-sm font-medium text-cerulean group-hover:underline mt-3">
                        Read more <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>

            </>
          ) : null}
        </div>
      </section>

      {/* Support Our Work */}
      <section className="py-16 px-4 bg-cerulean text-cerulean-foreground">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Support Our Work</h2>
          <p className="text-lg text-cerulean-foreground/90 leading-relaxed mb-10">
            FISH relies entirely on the generosity of our supporters and receives no public grants. Your donations help keep our minibus on the road—a lifeline for many.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="outline" className="border-2 border-cerulean-foreground text-cerulean-foreground bg-transparent hover:bg-cerulean-foreground hover:text-cerulean text-lg px-8 py-6 rounded-lg">
              <Link to="/get-involved/donations">Make a Donation</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-cerulean-foreground text-cerulean-foreground bg-transparent hover:bg-cerulean-foreground hover:text-cerulean text-lg px-8 py-6 rounded-lg">
              <Link to="/get-involved/legacies">Leave a Legacy</Link>
            </Button>
          </div>
        </div>
      </section>

    </Layout>
  );
}
